<script setup>
import { reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  knowledgeBase: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('assistantKnowledgeBases/getUIFlags'),
};

const state = reactive({
  name: '',
  description: '',
  kind: 'document_base',
});

// Р46: per-базовый выбор эмбеддинг-модели убран — он был «мёртвым» (Р11 п.8
// сделал модель единой на аккаунт, EmbeddingService per-KB настройку
// игнорирует). Провайдер и модель эмбеддингов задаются в «Настройках LLM».

// Р9.3: тип базы — «Документы» или «Вопрос-ответ». Выбирается только при
// создании; после создания тип не меняется (в edit-режиме скрыт).
const kindOptions = computed(() => [
  {
    id: 'document_base',
    label: t('ASSISTANT.KNOWLEDGE_BASES.KIND.DOCUMENT.LABEL'),
    description: t('ASSISTANT.KNOWLEDGE_BASES.KIND.DOCUMENT.DESCRIPTION'),
  },
  {
    id: 'qa_base',
    label: t('ASSISTANT.KNOWLEDGE_BASES.KIND.QA.LABEL'),
    description: t('ASSISTANT.KNOWLEDGE_BASES.KIND.QA.DESCRIPTION'),
  },
]);

const validationRules = {
  name: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(
  () =>
    formState.uiFlags.value.creatingItem || formState.uiFlags.value.updatingItem
);

const formErrors = computed(() => ({
  name: v$.value.name.$error
    ? t('ASSISTANT.KNOWLEDGE_BASES.FORM.NAME.ERROR')
    : '',
}));

const handleCancel = () => emit('cancel');

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }

  const payload = {
    name: state.name,
    description: state.description,
  };
  if (props.mode === 'create') {
    payload.kind = state.kind;
  }

  emit('submit', payload);
};

watch(
  () => props.knowledgeBase,
  knowledgeBase => {
    if (props.mode !== 'edit' || !knowledgeBase) return;

    Object.assign(state, {
      name: knowledgeBase.name || '',
      description: knowledgeBase.description || '',
      kind: knowledgeBase.kind || 'document_base',
    });
  },
  { immediate: true }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <div v-if="mode === 'create'" class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('ASSISTANT.KNOWLEDGE_BASES.KIND.LABEL') }}
      </span>
      <div class="flex flex-col gap-2">
        <RadioCard
          v-for="option in kindOptions"
          :id="option.id"
          :key="option.id"
          :label="option.label"
          :description="option.description"
          :is-active="state.kind === option.id"
          @select="state.kind = option.id"
        />
      </div>
    </div>

    <Input
      v-model="state.name"
      :label="t('ASSISTANT.KNOWLEDGE_BASES.FORM.NAME.LABEL')"
      :placeholder="t('ASSISTANT.KNOWLEDGE_BASES.FORM.NAME.PLACEHOLDER')"
      :message="formErrors.name"
      :message-type="formErrors.name ? 'error' : 'info'"
    />

    <TextArea
      v-model="state.description"
      :label="t('ASSISTANT.KNOWLEDGE_BASES.FORM.DESCRIPTION.LABEL')"
      :placeholder="t('ASSISTANT.KNOWLEDGE_BASES.FORM.DESCRIPTION.PLACEHOLDER')"
      :max-length="2000"
    />

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('ASSISTANT.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t(`ASSISTANT.FORM.${mode.toUpperCase()}`)"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
